// getSafeHref — scheme-validate a would-be hyperlink BEFORE it becomes an href,
// so a hostile/crafted value can never navigate (or script) a viewer through an
// unsafe scheme.
//
// Why this exists (CAR-F2 nemesis, 2026-07-15): the Image Carousel block
// (DL-041) renders a per-card <a href={hyperlink}> straight from node props.
// Those props deserialize from the saved-wheel API WITHOUT scheme validation, so
// a crafted saved payload (`showHyperlink:true`,
// `hyperlink:"javascript:alert(document.cookie)"`) would render an executable
// link chip — a click = stored-XSS on View Wheel — or a `//evil` protocol-
// relative open-redirect. Routing every hyperlink through this sanitizer at
// RENDER (regardless of what the authoring UI currently exposes) closes it.
//
// Mirrors the intent of the local `getSafeDocHref` in Help/FaqAccordionItem.jsx
// (the same class was fixed there before), promoted to a shared util so the
// carousel — and any future consumer — validates through ONE audited path.
//
// Policy — return the (trimmed) value ONLY when it is:
//   * an absolute http(s) URL              (https://example.com/…, http://…)
//   * an app-internal path, SINGLE leading slash (/wheel/123, /help)
// Reject everything else — javascript:, data:, vbscript:, file:, blob:, mailto:,
// tel:, AND protocol-relative "//host" (a cross-origin URL wearing a path's
// clothes). Returns null when unsafe, so the caller can gate rendering on it.

export function getSafeHref(href) {
  if (typeof href !== "string") return null;
  const trimmed = href.trim();
  if (!trimmed) return null;

  // "//host" or "/\host" is a PROTOCOL-RELATIVE cross-origin URL, not an app
  // path (browsers normalise a leading "/\" the same as "//" for special
  // schemes). Reject before the single-slash fast path can trust it.
  if (/^[/\\]{2}/.test(trimmed)) return null;

  // App-internal path — a single leading slash only. Same-origin, safe.
  if (trimmed.startsWith("/")) return trimmed;

  // Anything else must parse to an absolute http(s) URL. The URL constructor
  // strips embedded tab/newline and lower-cases the scheme, so obfuscations
  // like "java\tscript:" or "JavaScript:" still resolve to their real protocol
  // and get rejected here.
  try {
    const base =
      (typeof window !== "undefined" &&
        window.location &&
        window.location.origin) ||
      undefined;
    const url = new URL(trimmed, base);
    return url.protocol === "http:" || url.protocol === "https:"
      ? trimmed
      : null;
  } catch {
    return null;
  }
}
